/**
 * @fileoverview Per-route page metadata — the single source for both the
 * client-side `useSeo()` calls and the build-time HTML generator.
 *
 * Why one file: `useSeo` sets the title/description/canonical/OG tags from
 * JavaScript, which is fine for users and for Google (it renders JS), but the
 * scrapers behind link previews — Slack, LinkedIn, Twitter, iMessage — read the
 * raw HTML and stop. Every shared link therefore previewed with the same
 * generic title from public/index.html.
 *
 * `scripts/generate-route-html.js` fixes that by writing a real HTML file per
 * route at build time. It reads this module, so the preview a scraper sees and
 * the title a user sees can never disagree — which is exactly the class of drift
 * that produced the pricing-table contradictions.
 *
 * Plain CommonJS-compatible syntax (no JSX, no imports) so a Node build script
 * can require it directly without a transpile step.
 *
 * Dynamic routes are not here. Example pages' copy lives in the published
 * catalog, which the generator pulls from /list_examples the same way the
 * sitemap generator does. Industry pages' copy lives in industriesData.js; the
 * one piece they share with this module is their structured data, built by
 * `industryJsonLd()` below so the prerendered shell and the live page agree.
 */

const { API_LANDING_ENABLED } = require('./apiLanding');

/** The canonical public host. Every absolute URL in structured data uses it. */
const SITE_URL = 'https://www.optimiciti.com';

/** Appended to every page title. Kept out of the individual entries. */
const TITLE_SUFFIX = 'Optimiciti';

/** Used when a route has no entry, and as the site-wide default. */
const DEFAULT_META = {
  title: 'Optimiciti — Vehicle Routing & Scheduling Optimization',
  description:
    'Plan vehicle routes and schedules around the constraints you set — time windows, capacities, vehicle types, shift hours. Free to try, no account needed.',
};

/**
 * path -> { title, description }
 *
 * `title` is the bare page name; `pageTitle()` adds the suffix. A null title
 * means "use the full default title" (the home page, which shouldn't read
 * "Home — Optimiciti").
 */
const ROUTE_META = {
  '/home': {
    title: null,
    description: DEFAULT_META.description,
  },
  '/use-cases': {
    title: 'Examples',
    description:
      'See how businesses use Optimiciti to solve real-world logistics — technician routing, deliveries, and pickup/dropoff — with live, explorable example scenarios.',
  },
  '/pricing': {
    title: 'Pricing',
    description:
      // Not "every optimizer and constraint is free": the free tier does not
      // include resupply, multi-day, fixed assignments, per-vehicle-type travel
      // costs or road distances. Same correction as the page header itself.
      'Optimiciti pricing. Every optimizer is free to use; Professional adds bigger fleets, multi-day routes, resupply and real road distances.',
  },
  '/industries': {
    title: 'Industries',
    description:
      'See how Optimiciti plans real routes for field service, healthcare and medical delivery, and same-day couriers — every window, deadline, and vehicle handled.',
  },
  '/careers': {
    title: 'Careers',
    description:
      'Open roles at Optimiciti — building route optimization that dispatchers and owner-operators actually use. Remote, small team.',
  },
  '/model-builder': {
    title: 'Model builder',
    description:
      'Build a routing model from your own stops and vehicles — pick an optimizer, set your constraints, and solve in the browser. No account needed.',
  },
  '/models/vehicle-routing': {
    title: 'Vehicle routing',
    description:
      'Assign stops across a fleet and order each route around time windows, capacities and shift hours. Solve in the browser, free.',
  },
  '/models/pickup-dropoff': {
    title: 'Pickup & dropoff routing',
    description:
      'Plan work that comes in pairs — collected at one location, delivered to another — with pickup-before-dropoff enforced. Solve in the browser, free.',
  },
  /* Read by routes.js as well (via pageDescription), so the prerendered shell
     and the live page carry the same description. Deliberately plain: the
     policies themselves are hosted by Termly, and a description should not
     promise anything the document doesn't. */
  '/terms-of-service': {
    title: 'Terms of Service',
    description: 'The terms that apply when you use Optimiciti to plan routes and schedules.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'What information Optimiciti collects when you use it, and how that information is used.',
  },
};

/*
The /api landing page, added only once it is routable.

Both of this module's consumers are indexing machinery — useSeo() sets the tags a
reader and Google see, and scripts/generate-route-html.js writes a prerendered
HTML file for every key here, which is what link scrapers read. Adding /api while
the route still redirects would publish a title and description for a page that
does not exist, and prerender an /api/index.html that the redirect never serves.

So it arrives with the route. See src/utils/apiLanding.js for the flag and the
one thing flipping it does NOT do (robots.txt).
*/
if (API_LANDING_ENABLED) {
  ROUTE_META['/api'] = {
    title: 'Routing API',
    description:
      'Put route planning inside your own software. Send stops, vehicles and the rules they follow over HTTP, and get back who goes where, in what order, and when.',
  };
}

/** Full <title> for a route. */
function pageTitle(path) {
  const meta = ROUTE_META[path];
  if (!meta || !meta.title) return DEFAULT_META.title;
  return `${meta.title} | ${TITLE_SUFFIX}`;
}

/** Description for a route, falling back to the site default. */
function pageDescription(path) {
  const meta = ROUTE_META[path];
  return (meta && meta.description) || DEFAULT_META.description;
}

/**
 * Structured data for an industry landing page: a Service plus its breadcrumb.
 *
 * One builder for both consumers. IndustryLandingPage passes the result to
 * useSeo() at runtime, and scripts/generate-route-html.js bakes the same thing
 * into that route's prerendered shell, where useSeo() then adopts and replaces
 * it. Two hand-written copies would drift, and a crawler reading the shell would
 * see something different from the page.
 *
 * Only facts the page itself states: no ratings, reviews, prices or offers.
 *
 * @param {{slug: string, navLabel: string, seoDescription?: string}} industry
 * @param {string} [siteUrl] - Overridable for the build script's SITE_URL.
 */
function industryJsonLd(industry, siteUrl = SITE_URL) {
  const url = `${siteUrl}/industries/${industry.slug}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: `${industry.navLabel} route optimization`,
      provider: { '@type': 'Organization', name: 'Optimiciti', url: siteUrl },
      areaServed: 'Worldwide',
      description: industry.seoDescription,
      url,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Industries', item: `${siteUrl}/industries` },
        { '@type': 'ListItem', position: 2, name: industry.navLabel, item: url },
      ],
    },
  ];
}

module.exports = {
  SITE_URL,
  ROUTE_META,
  DEFAULT_META,
  TITLE_SUFFIX,
  pageTitle,
  pageDescription,
  industryJsonLd,
};
